import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PageHeader, EmptyState } from "@/components/shared";
import { Plus, Users2, Pencil, Trash2 } from "lucide-react";

const empty = { email: "", password: "", first_name: "", last_name: "", codice_fiscale: "", address: "", role: "employee", phone: "", department: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.first_name || !form.last_name || (!editId && (!form.email || !form.password))) {
      toast.error("Nome, cognome, email e password sono obbligatori"); return;
    }
    try {
      if (editId) {
        const payload = {
          first_name: form.first_name, last_name: form.last_name,
          codice_fiscale: form.codice_fiscale, address: form.address,
          role: form.role, phone: form.phone,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success(editId ? "Utente aggiornato" : "Utente creato");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente "${u.name}"?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Amministrazione" title="Dipendenti" subtitle="Gestisci utenti, ruoli e assegnatari">
        {isAdmin && (
          <Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="new-user-button">
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo utente
          </Button>
        )}
      </PageHeader>

      <Card className="rounded-lg overflow-hidden">
        {users.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className="label-caps">Nome</TableHead>
                  <TableHead className="label-caps hidden sm:table-cell">Email</TableHead>
                  <TableHead className="label-caps hidden md:table-cell">Codice fiscale</TableHead>
                  <TableHead className="label-caps">Ruolo</TableHead>
                  {isAdmin && <TableHead className="label-caps text-right">Azioni</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-semibold">{u.name}</TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="hidden md:table-cell">{u.codice_fiscale || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-md font-semibold ${u.role === "admin" ? "bg-primary/10 text-primary border-primary/25" : u.role === "viewer" ? "bg-chart-4/10 text-foreground border-border" : "bg-secondary text-foreground"}`}>
                        {u.role === "admin" ? "Admin" : u.role === "viewer" ? "Visualizzatore" : "Assegnatario"}
                      </Badge>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => { setForm({ ...empty, ...u, password: "" }); setEditId(u.id); setOpen(true); }} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState icon={Users2} title="Nessun utente" />
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="user-firstname-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Cognome *</Label>
              <Input data-testid="user-lastname-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editId} />
            </div>
            <div className="space-y-1.5">
              <Label>{editId ? "Nuova password" : "Password *"}</Label>
              <Input type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? "Lascia vuoto per non cambiare" : ""} />
            </div>
            <div className="space-y-1.5">
              <Label>Codice fiscale</Label>
              <Input data-testid="user-cf-input" value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5">
              <Label>Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Indirizzo di residenza</Label>
              <Input data-testid="user-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Es. Via Nazionale, 278 – Santa Maria a Vico (CE)" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Assegnatario</SelectItem>
                  <SelectItem value="viewer">Visualizzatore</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-user-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
